import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpXsrfTokenExtractor } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ZvooveProblemDetails } from '@zvoove-market/api';
import { AuthService, backendUrl } from '@zvoove-market/shared';
import { EMPTY, first, Observable, retry, Subject, throwError, timeout } from 'rxjs';

@Injectable()
export class HubHttpInterceptor implements HttpInterceptor {
  private xsrfTokenExtractor = inject(HttpXsrfTokenExtractor);

  private authService = inject(AuthService);
  private mappingPrefix = '{placeholder}';
  private silentLoginMessages = new Subject<boolean>();
  private silentLoginFrame: HTMLIFrameElement | null = null;

  constructor() {
    window.addEventListener('message', (e) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (e.data && e.data.source === 'bff-silent-login') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.silentLoginMessages.next(e.data.isLoggedIn);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!e.data.isLoggedIn) {
          // reload should trigger the login page in this case
          window.location.reload();
        }
      }
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(this.mappingPrefix)) {
      return next.handle(req);
    }
    let headers = req.headers;

    const xsrfToken = this.xsrfTokenExtractor.getToken();

    if (xsrfToken != null) {
      headers = headers.set('X-XSRF-TOKEN', xsrfToken);
    }

    const mandant = this.authService.$context().mandant;
    if (mandant) {
      headers = headers.set('X-Mandant-Id', mandant.id);
    }

    req = req.clone({
      url: backendUrl() + req.url.substring(this.mappingPrefix.length),
      headers: headers,
      withCredentials: true,
    });

    return next.handle(req).pipe(
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) => {
          if (error.status === 403 && (error.error as ZvooveProblemDetails)?.code === 'InvalidMandantId') {
            // Using the router did not work out, because auth.$context still and the current url still have the invalid one.
            // If I refresh auth.$context first, it will ignore my "null" for mandant id and use the one from the url.
            // If I navigate first, then auth.$context will still have the invalid one and the request for the available mandants on the mandant select page fails.
            window.location.href = window.location.origin + '/mandant-select';
            return EMPTY;
          }
          if (error.status !== 401) {
            return throwError(() => error);
          }

          if (!this.silentLoginFrame) {
            this.silentLoginFrame = document.createElement('iframe');
            this.silentLoginFrame.style.width = '0px';
            this.silentLoginFrame.style.height = '0px';
            this.silentLoginFrame.style.display = 'none';
            document.body.appendChild(this.silentLoginFrame);
          }

          this.silentLoginFrame.src = 'api/bff/silent-login';
          return this.silentLoginMessages.pipe(first(), timeout(10000));
        },
      })
    );
  }
}
